import { NavLink } from 'react-router';
import {
  LayoutDashboard,
  CalendarDays,
  CalendarRange,
  BarChart3,
  Banknote,
  ClipboardCheck,
  ClipboardList,
  CircleUser,
  FileSignature,
  FileText,
  Users,
  UserPlus,
  Network,
  ScanSearch,
  Palmtree,
  Repeat,
  Activity as ActivityIcon,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import type { ModuleFlags, ModuleKey, Permission } from '@pointage/shared';
import { useUiStore } from '../store/ui.js';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
  /** Effective permission required to see this item. Default: any staff. */
  perm?: Permission;
  /** Feature module that must be enabled (Paramètres → Modules). */
  module?: ModuleKey;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// Mockup nav grouping (Quotidien / Données / Système), French labels.
// Visibility is driven by the session's EFFECTIVE permissions (role ±
// per-user overrides) and the module flags — not by hardcoded roles.
const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Quotidien',
    items: [
      { to: '/dashboard', label: 'Tableau de bord', Icon: LayoutDashboard },
      { to: '/bureau', label: 'Bureau', Icon: ClipboardList, perm: 'bureau.view' },
      { to: '/entry', label: 'Saisie quotidienne', Icon: CalendarDays },
      { to: '/monthly', label: 'Grille mensuelle', Icon: CalendarRange },
    ],
  },
  {
    label: 'Données',
    items: [
      { to: '/employees', label: 'Employés', Icon: Users },
      { to: '/organigramme', label: 'Organigramme', Icon: Network },
      { to: '/inspect', label: 'Inspection', Icon: ScanSearch },
      { to: '/balances', label: 'Congés', Icon: Palmtree },
      { to: '/recup', label: 'Récupération', Icon: Repeat },
      { to: '/contrats', label: "Période d'essai & CDD", Icon: FileSignature, module: 'contrats' },
      { to: '/documents', label: 'Demandes documents', Icon: FileText, module: 'documents' },
      { to: '/reports', label: 'Rapports & fiches', Icon: BarChart3 },
      { to: '/activity', label: 'Activité', Icon: ActivityIcon, perm: 'activity.view' },
    ],
  },
  {
    label: 'Paie',
    items: [
      // Salaries are confidential — payroll.view only.
      { to: '/salaires', label: 'Salaires & bonus', Icon: Banknote, perm: 'payroll.view', module: 'payroll' },
    ],
  },
  {
    label: 'Talent',
    items: [
      // Performance data is confidential — evaluations.view only.
      { to: '/evaluations', label: 'Évaluations', Icon: ClipboardCheck, perm: 'evaluations.view', module: 'evaluations' },
      { to: '/recrutement', label: 'Recrutement', Icon: UserPlus, perm: 'recrutement.view', module: 'recrutement' },
    ],
  },
  {
    label: 'Système',
    items: [
      { to: '/settings', label: 'Paramètres', Icon: SettingsIcon, perm: 'settings.edit' },
    ],
  },
  {
    label: 'Personnel',
    items: [
      // Opens the Espace employé for the signed-in staff member's own
      // fiche (matched by email) — outside the admin shell.
      { to: '/espace', label: 'Mon espace employé', Icon: CircleUser, module: 'espace' },
    ],
  },
];

interface Props {
  permissions: readonly Permission[];
  modules: ModuleFlags | null;
}

export function Sidebar({ permissions, modules }: Props) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const drawerOpen = useUiStore((s) => s.drawerOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const closeDrawer = useUiStore((s) => s.closeDrawer);

  const canSee = (item: NavItem) =>
    (!item.perm || permissions.includes(item.perm)) &&
    (!item.module || !modules || modules[item.module]);

  return (
    <aside
      className="sidebar"
      data-collapsed={collapsed}
      data-drawer-open={drawerOpen}
      data-testid="sidebar"
    >
      <div className="brand">
        <div className="brand-mark" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 3 H13 V6 H9 V10 H13 V13 H3 V10 H7 V6 H3 Z" fill="white" />
          </svg>
        </div>
        <div>
          <div className="brand-name">Pointage</div>
          <div className="brand-sub">SHIFTIN group</div>
        </div>
      </div>

      <nav aria-label="Navigation principale">
        {NAV_SECTIONS.map((section) => {
          const visible = section.items.filter(canSee);
          if (visible.length === 0) return null;
          return (
            <div className="nav-section" key={section.label}>
              <div className="nav-label">{section.label}</div>
              {visible.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={closeDrawer}
                  // Rail réduit : le libellé est masqué, le title natif prend
                  // le relais (une infobulle absolue serait rognée par le
                  // défilement du nav).
                  title={collapsed ? label : undefined}
                  className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                >
                  <Icon className="nav-icon" aria-hidden="true" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-bottom">
        <button
          type="button"
          className="icon-button sidebar-collapse-btn"
          onClick={toggleSidebar}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight aria-hidden="true" /> : <ChevronLeft aria-hidden="true" />}
        </button>
      </div>
    </aside>
  );
}
